/**
 * The run's copies of the Pets v3 abilities (docs/PETS.md "Pets v3"): the general kinds the five newer pets use, each
 * resolved at the pet's level (meta/petAbilities.ts petRule: damage and burn × the towers' level curve). They ride on
 * `PetRule` as optional fields (`strike`, `aura`, `pulse`, `signature`) beside the pup's v2 ones, and join the state
 * hash and the replay record only when present, so a run with the pup, or no pet, hashes as it always did.
 */
import { DamageType } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import { Fnv1a64 } from "../maths/fnv1a64";

/** What an ability does to each raider it touches; zeros are none of it. */
export interface PetHit {
  readonly damage: Fix64;
  readonly damageType: DamageType;
  readonly burnPerTick: Fix64;
  readonly burnTicks: number;
  readonly slow: Fix64;
  readonly slowTicks: number;
  readonly stunTicks: number;
  readonly bossStunTicks: number;
  readonly markTicks: number;
  readonly markTakenPercent: number;
  readonly markPointsPercent: number;
  readonly dazzleTicks: number;
}
/** Strike targets: ground raiders, any raider, or a flyer first. */
export enum StrikeTarget { Ground = 0, Any = 1, AirFirst = 2 }
export interface StrikeRule { readonly intervalTicks: number; readonly reach: Fix64; readonly targets: StrikeTarget; readonly airMultPercent: number; readonly hit: PetHit }
export interface AuraRule {
  readonly reach: Fix64; readonly towerAirPercent: number; readonly burningTakenPercent: number; readonly troopGuardPercent: number;
  readonly healEveryTicks: number; readonly healPercent: number; readonly killPointsPercent: number;
}
export interface PulseRule {
  readonly everyTicks: number; readonly reach: Fix64; readonly air: boolean; readonly strongest: boolean;
  readonly troopShieldPercent: number; readonly shieldTicks: number; readonly troopHealPercent: number; readonly hit: PetHit;
}
export interface SignatureRule { readonly reach: Fix64; readonly air: boolean; readonly strongest: boolean; readonly hit: PetHit }

/** The v3 fields of a PetRule. */
export interface PetKit { readonly strike?: StrikeRule; readonly aura?: AuraRule; readonly pulse?: PulseRule; readonly signature?: SignatureRule }

export function hitTouches(h: PetHit): boolean {
  return h.damage.isPositive() || h.burnTicks > 0 || h.slowTicks > 0 || h.stunTicks > 0 || h.markTicks > 0 || h.dazzleTicks > 0;
}

const STRIKE_TAG = 0x57e1, AURA_TAG = 0xa0e1, PULSE_TAG = 0x9a1e, SIGNATURE_TAG = 0x5191;

function hashHit(h: Fnv1a64, x: PetHit): void {
  h.writeRaw(x.damage.raw); h.writeInt(x.damageType); h.writeRaw(x.burnPerTick.raw); h.writeInt(x.burnTicks); h.writeRaw(x.slow.raw); h.writeInt(x.slowTicks);
  h.writeInt(x.stunTicks); h.writeInt(x.bossStunTicks); h.writeInt(x.markTicks); h.writeInt(x.markTakenPercent); h.writeInt(x.markPointsPercent); h.writeInt(x.dazzleTicks);
}

/** The kit's block of the hash (nothing for a pet without one: the pup). */
export function hashPetKit(h: Fnv1a64, k: PetKit): void {
  if (k.strike) { const s = k.strike; h.writeInt(STRIKE_TAG); h.writeInt(s.intervalTicks); h.writeRaw(s.reach.raw); h.writeInt(s.targets); h.writeInt(s.airMultPercent); hashHit(h, s.hit); }
  if (k.aura) {
    const a = k.aura;
    h.writeInt(AURA_TAG); h.writeRaw(a.reach.raw); h.writeInt(a.towerAirPercent); h.writeInt(a.burningTakenPercent); h.writeInt(a.troopGuardPercent); h.writeInt(a.healEveryTicks); h.writeInt(a.healPercent); h.writeInt(a.killPointsPercent);
  }
  if (k.pulse) {
    const p = k.pulse;
    h.writeInt(PULSE_TAG); h.writeInt(p.everyTicks); h.writeRaw(p.reach.raw); h.writeBool(p.air); h.writeBool(p.strongest); h.writeInt(p.troopShieldPercent); h.writeInt(p.shieldTicks); h.writeInt(p.troopHealPercent); hashHit(h, p.hit);
  }
  if (k.signature) { const s = k.signature; h.writeInt(SIGNATURE_TAG); h.writeRaw(s.reach.raw); h.writeBool(s.air); h.writeBool(s.strongest); hashHit(h, s.hit); }
}

// ---- the replay record: Fix64 numbers as raw decimal strings, the rest as they are

type Json = Record<string, unknown>;
const rawOf = (f: Fix64): string => f.raw.toString();
const obj = (v: unknown): v is Json => typeof v === "object" && v !== null && !Array.isArray(v);
const whole = (v: unknown, min = 0, max = Number.MAX_SAFE_INTEGER): v is number => Number.isInteger(v) && (v as number) >= min && (v as number) <= max;
const raw = (v: unknown, allowZero = false): Fix64 | null => (typeof v === "string" && /^\d{1,30}$/.test(v) && (allowZero || BigInt(v) > 0n) ? Fix64.fromRaw(BigInt(v)) : null);
const MAX_PERCENT = 500, MAX_CUT = 90;

function hitToJson(x: PetHit): Json { return { ...x, damage: rawOf(x.damage), burnPerTick: rawOf(x.burnPerTick), slow: rawOf(x.slow) }; }
function hitFromJson(v: unknown): PetHit | null {
  if (!obj(v)) return null;
  const damage = raw(v.damage, true), burnPerTick = raw(v.burnPerTick, true), slow = raw(v.slow, true);
  if (damage === null || burnPerTick === null || slow === null || !whole(v.damageType, 0, 2) || slow.gt(Fix64.fromFraction(MAX_CUT, 100))) return null;
  const ints = ["burnTicks", "slowTicks", "stunTicks", "bossStunTicks", "markTicks", "markTakenPercent", "markPointsPercent", "dazzleTicks"] as const;
  if (!ints.every((k) => whole(v[k], 0, k.endsWith("Percent") ? MAX_PERCENT : Number.MAX_SAFE_INTEGER))) return null;
  return {
    damage, damageType: v.damageType as DamageType, burnPerTick, burnTicks: v.burnTicks as number, slow, slowTicks: v.slowTicks as number, stunTicks: v.stunTicks as number,
    bossStunTicks: v.bossStunTicks as number, markTicks: v.markTicks as number, markTakenPercent: v.markTakenPercent as number, markPointsPercent: v.markPointsPercent as number, dazzleTicks: v.dazzleTicks as number,
  };
}

export interface PetKitJson { strike?: Json; aura?: Json; pulse?: Json; signature?: Json }

export function petKitToJson(k: PetKit): PetKitJson {
  const out: PetKitJson = {};
  if (k.strike) out.strike = { ...k.strike, reach: rawOf(k.strike.reach), hit: hitToJson(k.strike.hit) };
  if (k.aura) out.aura = { ...k.aura, reach: rawOf(k.aura.reach) };
  if (k.pulse) out.pulse = { ...k.pulse, reach: rawOf(k.pulse.reach), hit: hitToJson(k.pulse.hit) };
  if (k.signature) out.signature = { ...k.signature, reach: rawOf(k.signature.reach), hit: hitToJson(k.signature.hit) };
  return out;
}

/** The kit of a stored pet block (absent fields: none); null when one is malformed. */
export function petKitFromJson(v: Json): PetKit | null {
  let k: PetKit = {};
  if (v.strike !== undefined) {
    const s = v.strike, reach = obj(s) ? raw(s.reach) : null, hit = obj(s) ? hitFromJson(s.hit) : null;
    if (!obj(s) || reach === null || hit === null || !whole(s.intervalTicks, 1) || !whole(s.targets, 0, 2) || !whole(s.airMultPercent, 1, MAX_PERCENT)) return null;
    k = { ...k, strike: { intervalTicks: s.intervalTicks, reach, targets: s.targets as StrikeTarget, airMultPercent: s.airMultPercent, hit } };
  }
  if (v.aura !== undefined) {
    const a = v.aura, reach = obj(a) ? raw(a.reach) : null;
    const pcts = ["towerAirPercent", "burningTakenPercent", "killPointsPercent"] as const;
    if (!obj(a) || reach === null || !pcts.every((p) => whole(a[p], 0, MAX_PERCENT)) || !whole(a.troopGuardPercent, 0, MAX_CUT) || !whole(a.healEveryTicks) || !whole(a.healPercent, 0, 100)) return null;
    k = { ...k, aura: { reach, towerAirPercent: a.towerAirPercent as number, burningTakenPercent: a.burningTakenPercent as number, troopGuardPercent: a.troopGuardPercent, healEveryTicks: a.healEveryTicks, healPercent: a.healPercent, killPointsPercent: a.killPointsPercent as number } };
  }
  if (v.pulse !== undefined) {
    const p = v.pulse, reach = obj(p) ? raw(p.reach) : null, hit = obj(p) ? hitFromJson(p.hit) : null;
    if (!obj(p) || reach === null || hit === null || !whole(p.everyTicks, 1) || typeof p.air !== "boolean" || typeof p.strongest !== "boolean" || !whole(p.troopShieldPercent, 0, MAX_PERCENT) || !whole(p.shieldTicks) || !whole(p.troopHealPercent, 0, 100)) return null;
    k = { ...k, pulse: { everyTicks: p.everyTicks, reach, air: p.air, strongest: p.strongest, troopShieldPercent: p.troopShieldPercent, shieldTicks: p.shieldTicks, troopHealPercent: p.troopHealPercent, hit } };
  }
  if (v.signature !== undefined) {
    const s = v.signature, reach = obj(s) ? raw(s.reach) : null, hit = obj(s) ? hitFromJson(s.hit) : null;
    if (!obj(s) || reach === null || hit === null || typeof s.air !== "boolean" || typeof s.strongest !== "boolean") return null;
    k = { ...k, signature: { reach, air: s.air, strongest: s.strongest, hit } };
  }
  return k;
}
